import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";

export default function HighMountMadisonProposalTemplate() {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      
      <main className="flex-grow bg-white">
        <div className="max-w-4xl mx-auto px-6 py-12">
          {/* Header */}
          <div className="mb-12">
            <h1 className="text-3xl font-serif font-medium text-indigo-950 mb-4">
              Proposal for Strategic Advisory Partnership with 4Ever Young (High Mount Madison)
            </h1>
          </div>

          <hr className="border-gray-400 mb-12" />

          {/* Executive Summary */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-4">
              Executive Summary
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                4Ever Young is uniquely positioned: the country's largest anti-aging and med spa franchise, ready to scale beyond 100 locations and set the bar for quality, consistency, and differentiation in a fast-crowding space. The path forward calls for both sharper strategic direction and hands-on clinical guidance—blending proven, standardized protocols with a bold, unifying brand message. Now is the time to equip your executive team and franchisees with the tools, digital infrastructure, and operational expertise to move decisively from franchise-to-franchise growth toward a cohesive, premium care network.
              </p>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Engagement Options */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-6">
              Engagement Options
            </h2>

            {/* Comparison Table */}
            <div className="overflow-x-auto mb-8">
              <table className="w-full border-collapse border border-gray-400">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Tier</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Scope</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Cadence</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Investment</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Success Looks Like</th>
                    <th className="border border-gray-400 px-4 py-3 text-left font-bold text-gray-900">Quick Wins for Forever Young</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-400 px-4 py-3 font-bold">Operating Advisor</td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Up to 4h/month executive team strategy; clinical/business protocol library; quarterly KPI/action review
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Monthly/Quarterly
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-lg font-bold">
                      $75,000/year
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Executive team moves decisively, franchisees adopt consistent protocols, clinical/service upgrades land quickly
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Full access to SOPs, protocols, slide decks; guided overhaul of clinical program and franchisee playbook; clear KPI tracking
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-gray-400 px-4 py-3 font-bold">Strategic Partnership</td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Everything in Operating Advisor; tailored protocol/workflow customization; digital tool deployment; annual innovation sprint
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Monthly/Quarterly + Project-based
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-lg font-bold">
                      $150,000/year
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Clinic network runs on unified, digital-ready clinical pathways; scalable quality; data-powered operations; distinctive patient journey
                    </td>
                    <td className="border border-gray-400 px-4 py-3 text-sm">
                      Rollout of AI-powered diagnostic report generator, custom patient portal, business dashboards; annual innovation sprint targeting highest priority
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Deliverables and Approach */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-6">
              Deliverables and Approach
            </h2>

            {/* Operating Advisor */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-indigo-950 mb-4">
                Operating Advisor ($75,000/year)
              </h3>
              <ul className="list-disc ml-6 space-y-2 text-gray-700">
                <li>Up to 4 hours/month in-depth collaboration with your executive team (virtual)</li>
                <li>Direct, actionable strategic input for clinical and business priorities, including:
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Comprehensive review and rollout support for all major clinical programs (TRT, peptides, HRT, IVs, GLP-1s, supplementation)</li>
                    <li>Brand and service roadmap development: zeroing in on target customer archetypes and high-ROI growth strategies</li>
                    <li>Market and operational insight: specific, pragmatic advice to avoid missteps common in franchise scaling and care model evolution</li>
                    <li>Quarterly KPI review and action plan to keep progress visible and priorities focused</li>
                  </ul>
                </li>
                <li>Full library access—protocols, training decks, operational SOPs—for leadership and franchisees</li>
                <li>Targeted workshops or content for internal training, as needed</li>
              </ul>

              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mt-4">
                <h4 className="font-semibold text-blue-900 mb-2">What Success Looks Like:</h4>
                <ul className="list-disc ml-6 space-y-1 text-blue-800">
                  <li>Franchisees run with a clear, best-in-class clinical playbook—delivering uniform, high-quality outcomes</li>
                  <li>Leadership steers the business with clarity and confidence—backed by timely, strategic input</li>
                  <li>Key growth and operational goals convert from slide decks into reality</li>
                </ul>
              </div>
            </div>

            <hr className="border-gray-300 mb-8" />

            {/* Strategic Partnership */}
            <div className="mb-8">
              <h3 className="text-lg font-semibold text-indigo-950 mb-4">
                Strategic Partnership ($150,000/year)
              </h3>
              <p className="text-gray-700 mb-4">Everything above, plus:</p>
              
              <ul className="list-disc ml-6 space-y-2 text-gray-700">
                <li><strong>Bespoke Protocol & Workflow Customization:</strong> I'll work hands-on to adapt protocols, SOPs, and service models for 4Ever Young's unique brand, patient profile, and franchise structure.</li>
                <li><strong>Digital Tool Integration:</strong> Deploy digital assets and tools for operational consistency and efficiency, including:
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Automated, branded diagnostic report generator—ready for network-wide rollout</li>
                    <li>AI-powered patient portal—streamlined, compliant, and engaging</li>
                    <li>Executive and franchisee-facing dashboards—turning data into action</li>
                  </ul>
                </li>
                <li><strong>Annual Innovation Sprint:</strong> One intensive, virtual or onsite strategy sprint to fast-track a high-impact project: launch telehealth, reimagine a core service, or solve an operational bottleneck.</li>
              </ul>

              <div className="bg-green-50 p-4 rounded-lg border border-green-200 mt-4">
                <h4 className="font-semibold text-green-900 mb-2">What Success Looks Like:</h4>
                <ul className="list-disc ml-6 space-y-1 text-green-800">
                  <li>Clinics operate on a single, unified playbook—delivering seamless care regardless of geography or staff</li>
                  <li>Digital backbone enables scalable growth—easy onboarding, rapid protocol adoption, actionable data</li>
                  <li>4Ever Young sets the industry pace for patient experience, outcomes, and operational excellence</li>
                </ul>
              </div>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Next Steps */}
          <section className="mb-12">
            <h2 className="text-xl font-serif font-medium text-indigo-950 mb-4">
              Recommended Next Step
            </h2>
            <div className="prose prose-lg text-gray-700 leading-relaxed space-y-4">
              <p>
                Let's set up a 30-minute review to align on priorities, confirm scope, and finalize the Statement of Work. Once agreed, I'll immediately onboard your team, launch discovery, and map out the first set of deliverables.
              </p>
              <p>
                Looking forward to elevating 4Ever Young from a category leader to a category definer.
              </p>
            </div>
          </section>

          <hr className="border-gray-400 mb-12" />

          {/* Contact Information */}
          <section className="text-left">
            <div className="text-gray-700">
              <p><em>Prepared by Anant Vinjamoori, MD MBA</em></p>
              <p>President, Next Generation Medicine</p>
              <p><a href="http://www.nextgenerationmedicine.co" className="text-blue-600 hover:text-blue-800">www.nextgenerationmedicine.co</a></p>
              <p>Contact: <a href="mailto:anant@nextgenerationmedicine.co" className="text-blue-600 hover:text-blue-800">anant@nextgenerationmedicine.co</a></p>
            </div>
          </section>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}